import React, { useState } from 'react';
import { Play, Download, Crown } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/export';

const s = {
  page: { padding: '2rem', maxWidth: 1200, margin: '0 auto' },
  h1: { fontFamily: 'Playfair Display, serif', color: 'var(--text)', fontSize: '1.75rem', marginBottom: 4 },
  desc: { fontFamily: 'DM Sans, sans-serif', color: 'var(--secondary)', fontSize: '0.9rem', marginBottom: '1.5rem', maxWidth: 700, lineHeight: 1.6 },
  btn: { fontFamily: 'DM Sans, sans-serif', background: 'var(--primary)', color: 'var(--bg)', border: 'none', padding: '10px 24px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', display: 'inline-flex', alignItems: 'center', gap: 8 },
  btnOutline: { fontFamily: 'DM Sans, sans-serif', background: 'transparent', color: 'var(--secondary)', border: '1px solid var(--secondary)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 6 },
  actions: { display: 'flex', gap: 12, marginBottom: '1.5rem', alignItems: 'center' },
  card: { background: 'var(--card-bg)', borderRadius: 12, padding: '1.5rem', marginBottom: '1.5rem', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontFamily: 'DM Sans, sans-serif' },
  th: { textAlign: 'left', padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.1)', color: 'var(--secondary)', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  td: { padding: '10px 14px', borderBottom: '1px solid rgba(242,237,228,0.05)', color: 'var(--text)', fontSize: '0.88rem' },
  spinner: { display: 'inline-block', width: 18, height: 18, border: '2px solid var(--primary)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  sortBtn: { background: 'none', border: 'none', color: 'var(--secondary)', cursor: 'pointer', fontSize: '0.7rem', marginLeft: 4 },
};

const fallbackData = [
  { name: 'Jake T.', currentTier: 'Bloom', probability: 0.94, daysToHarvest: 28, monthlySpend: '$148', visitFreq: '5.2/wk', keyDriver: 'Consistent high-AOV visits' },
  { name: 'Emma W.', currentTier: 'Bloom', probability: 0.89, daysToHarvest: 35, monthlySpend: '$135', visitFreq: '4.8/wk', keyDriver: 'Growing add-on purchases' },
  { name: 'Aisha B.', currentTier: 'Bloom', probability: 0.85, daysToHarvest: 42, monthlySpend: '$128', visitFreq: '4.5/wk', keyDriver: 'Multi-daypart visits' },
  { name: 'Carlos R.', currentTier: 'Bloom', probability: 0.81, daysToHarvest: 55, monthlySpend: '$122', visitFreq: '4.1/wk', keyDriver: 'Weekend + weekday pattern' },
  { name: 'Priya N.', currentTier: 'Bloom', probability: 0.76, daysToHarvest: 62, monthlySpend: '$118', visitFreq: '3.8/wk', keyDriver: 'Specialty drink loyalty' },
  { name: 'Noah D.', currentTier: 'Bloom', probability: 0.72, daysToHarvest: 70, monthlySpend: '$112', visitFreq: '3.5/wk', keyDriver: 'Increasing visit cadence' },
  { name: 'Maria L.', currentTier: 'Seedling', probability: 0.68, daysToHarvest: 85, monthlySpend: '$95', visitFreq: '3.2/wk', keyDriver: 'Rapid acceleration pattern' },
  { name: 'Wei Z.', currentTier: 'Bloom', probability: 0.65, daysToHarvest: 88, monthlySpend: '$108', visitFreq: '3.0/wk', keyDriver: 'Steady upward trajectory' },
];

export default function VIPLikely() {
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('probability');
  const [sortDir, setSortDir] = useState('desc');

  const runModel = async () => {
    setLoading(true);
    try {
      const res = await api.post('/models/vip-likely');
      setResults(res.data?.results || fallbackData);
      toast.success('VIP prediction complete');
    } catch {
      setResults(fallbackData);
      toast.success('VIP prediction complete (demo data)');
    } finally {
      setLoading(false);
    }
  };

  const sorted = [...(results || [])].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });
  const toggleSort = (key) => { if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortKey(key); setSortDir('desc'); } };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      <h1 style={s.h1}>VIP Likely</h1>
      <p style={s.desc}>
        Predicts which customers will reach Harvest tier within the next 90 days. Enables proactive
        white-glove treatment to accelerate and celebrate their journey.
      </p>

      <div style={s.actions}>
        <button style={s.btn} onClick={runModel} disabled={loading}>
          {loading ? <><span style={s.spinner} /> Running...</> : <><Play size={16} /> Run Model</>}
        </button>
        {results && (
          <button style={s.btnOutline} onClick={() => exportToCSV(results, 'vip_likely')}>
            <Download size={14} /> Export CSV
          </button>
        )}
      </div>

      {results && (
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                {[['name','Customer'],['currentTier','Current Tier'],['probability','Probability'],['daysToHarvest','Days to Harvest'],['monthlySpend','Monthly Spend'],['visitFreq','Visit Freq'],['keyDriver','Key Driver']].map(([k,l]) => (
                  <th key={k} style={s.th}>{l}<button style={s.sortBtn} onClick={() => toggleSort(k)}>{sortKey === k ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}</button></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr key={i}>
                  <td style={s.td}>{row.name}</td>
                  <td style={s.td}>{row.currentTier}</td>
                  <td style={{ ...s.td, color: 'var(--primary)', fontWeight: 600 }}>{(row.probability * 100).toFixed(0)}%</td>
                  <td style={s.td}>{row.daysToHarvest}</td>
                  <td style={s.td}>{row.monthlySpend}</td>
                  <td style={s.td}>{row.visitFreq}</td>
                  <td style={s.td}>{row.keyDriver}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
